<template>
  <div class="item-card" :class="cardClasses">
    <div class="item-content">
      <div class="item-header">
        <!-- Drag Handle -->
        <q-icon
          v-if="showDragHandle"
          name="drag_indicator"
          class="drag-handle cursor-move q-mr-sm"
        />

        <!-- Item Name -->
        <div class="item-name">{{ displayName }}</div>

        <!-- Action Buttons -->
        <div v-if="showActions" class="item-actions">
          <slot name="actions">
            <!-- Default action button - can be overridden -->
            <q-btn
              flat
              round
              :color="actionColor"
              :icon="actionIcon"
              size="sm"
              @click="$emit('action', item)"
            >
              <q-tooltip>{{ actionTooltip }}</q-tooltip>
            </q-btn>
          </slot>
        </div>
      </div>

      <!-- Part Number -->
      <div v-if="item.part_number" class="item-part-number">
        {{ item.part_number }}
      </div>

      <!-- Item Details -->
      <div class="item-details">
        <!-- Price Information -->
        <div class="item-price">
          {{ item.quantity }} × {{ formatCurrency(item.unit_price) }}
          <span v-if="item.markup" class="markup-info"> ({{ item.markup }}% markup)</span>
        </div>

        <!-- Badges -->
        <div class="item-badges">
          <!-- Show labor type instead of generic "Labor" -->
          <q-badge
            v-if="isLaborItem"
            color="accent"
            class="q-mr-xs"
          >
            {{ laborTypeDisplay }}
          </q-badge>

          <!-- Only show type badge if NOT labor (to avoid duplication) -->
          <q-badge
            v-if="item.type && item.type !== 'labor'"
            color="secondary"
            class="q-mr-xs"
          >
            {{ item.type }}
          </q-badge>

          <q-badge
            v-if="item.is_group && item.item_count"
            color="primary"
            class="q-mr-xs"
          >
            {{ item.item_count }} items
          </q-badge>

          <!-- Additional custom badges slot -->
          <slot name="badges"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue';

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    itemType: {
      type: String,
      default: 'regular',
      validator: (value) => ['regular', 'labor', 'available', 'section'].includes(value),
    },
    showDragHandle: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    actionIcon: {
      type: String,
      default: 'add',
    },
    actionColor: {
      type: String,
      default: 'primary',
    },
    actionTooltip: {
      type: String,
      default: 'Add to section',
    },
  });

  defineEmits(['action', 'edit', 'delete', 'add', 'remove']);

  const displayName = computed(() => {
    return props.item.description ||
      props.item.product_name ||
      props.item.name ||
      props.item.part_number ||
      'Unnamed Item';
  });

  const isLaborItem = computed(() => {
    return props.item.is_labor ||
      props.item.item_type === 'labor' ||
      props.item.type === 'labor' ||
      props.item.fromLabor;
  });

  const laborTypeDisplay = computed(() => {
    if (!isLaborItem.value) {return null;}

    // Check multiple sources for labor type (both snake_case and camelCase)
    const laborType = props.item.labor_type ||
      props.item.metadata?.original_labor_type ||  // snake_case
      props.item.metadata?.laborType ||             // camelCase
      props.item.metadata?.labor_type ||
      'Labor';

    // Format the display text (replace underscores with spaces, capitalize)
    return laborType
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  });

  const cardClasses = computed(() => {
    return {
      'item-card--compact': props.compact,
      'item-card--labor': isLaborItem.value,
      'item-card--section': props.itemType === 'section',
      'item-card--available': props.itemType === 'available',
    };
  });

  function formatCurrency(value) {
    const num = parseFloat(value || 0);
    return `$${num.toFixed(2)}`;
  }
</script>

<style scoped>
.item-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.item-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #1976d2;
}

.item-card--compact .item-content {
  padding: 8px;
}

.item-card--section {
  border-left: 3px solid #1976d2;
}

.item-content {
  padding: 12px;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.item-name {
  flex: 1;
  font-weight: 500;
  font-size: 14px;
  color: #1a1a1a;
}

.item-actions {
  display: flex;
  gap: 4px;
}

.drag-handle {
  cursor: grab;
  color: #9e9e9e;
}

.drag-handle:active {
  cursor: grabbing;
}

.item-part-number {
  font-size: 12px;
  color: #616161;
  margin-bottom: 8px;
  font-family: monospace;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-price {
  font-size: 13px;
  color: #424242;
  font-weight: 500;
}

.markup-info {
  font-size: 11px;
  color: #757575;
  font-weight: normal;
}

.item-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
